import { DollarSign } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { formatCurrency } from '@/lib/formatters';

interface FinancialField {
  key: string;
  label: string;
  value: number | string | null | undefined;
  isCurrency?: boolean;
  highlight?: boolean;
  type?: 'normal' | 'success' | 'warning' | 'danger';
}

interface ShipmentFinancialsProps {
  fields: FinancialField[];
  title?: string;
  className?: string;
  columns?: 1 | 2;
}

export function ShipmentFinancials({
  fields,
  title = 'المعلومات المالية',
  className,
  columns = 2,
}: ShipmentFinancialsProps) {
  const getValueClass = (type?: FinancialField['type']) => {
    switch (type) {
      case 'success':
        return 'text-green-600 dark:text-green-400';
      case 'warning':
        return 'text-yellow-600 dark:text-yellow-400';
      case 'danger':
        return 'text-red-600 dark:text-red-400';
      default:
        return '';
    }
  };

  return (
    <Card className={cn("h-full", className)}>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base font-semibold">
          <DollarSign className="h-4 w-4" />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className={cn(
          "grid gap-3",
          columns === 1 ? "grid-cols-1" : "grid-cols-1 sm:grid-cols-2"
        )}>
          {fields.map((field) => (
            <div
              key={field.key}
              className={cn(
                "flex justify-between items-center py-2 px-3 rounded-lg",
                field.highlight ? "bg-primary/10" : "bg-muted/50"
              )}
            >
              <span className="text-sm text-muted-foreground">{field.label}</span>
              <span
                className={cn(
                  "text-sm font-medium",
                  field.highlight && "text-primary font-semibold",
                  getValueClass(field.type)
                )}
              >
                {field.isCurrency !== false && typeof field.value === 'number'
                  ? formatCurrency(field.value)
                  : field.value ?? '-'}
              </span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
